import { GeneralApiEndpoints } from '@AppCore/constants/api-endpoints';
import { GlobalUtilityService } from '@AppCore/services/global-utility.service';
import { HttpHelperService } from '@AppCore/services/http-helper.service';
import { environment } from '@Environments/environment';
import { Injectable } from '@angular/core';
import {
  ReplaySubject,
  Observable,
  catchError,
  tap,
  BehaviorSubject,
  map,
  of,
} from 'rxjs';
import { APIResponse, productParams } from '../models/api-response.interface';
import {
  CartItems,
  cartSummary,
  categories,
  MashhoorplusImages,
} from '../models/common.interface';
import { LocalStorageDataModel } from '@AppCore/constants/local-storage-data-model';
import { mashhoorplusImages } from '../dummy-data/data';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  cartItemCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  cartUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  blogListSubject = new BehaviorSubject<any[] | null>(null);
  blogList$ = this.blogListSubject.asObservable();
  cartItems: BehaviorSubject<Array<CartItems>> = new BehaviorSubject<
    Array<CartItems>
  >([]);
  searchOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  categoriesList: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  mashhoorplusImages: BehaviorSubject<Array<MashhoorplusImages>> =
    new BehaviorSubject<Array<MashhoorplusImages>>([]);
  cartSummary: BehaviorSubject<cartSummary | null> =
    new BehaviorSubject<cartSummary | null>(null);

  cartOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  constructor(
    private http: HttpHelperService,
    private globalUtilityService: GlobalUtilityService
  ) {}

  setCartOpen(value: boolean) {
    this.cartOpen.next(value);
    this.globalUtilityService.hideLoader();
  }

  getCartOpen(): Observable<boolean> {
    return this.cartOpen.asObservable();
  }

  setSearchOpen(value: boolean) {
    this.searchOpen.next(value);
  }

  getSearchOpen(): Observable<boolean> {
    return this.searchOpen.asObservable();
  }

  setCartItems(cartItems: any) {
    this.cartItems.next(cartItems);
  }

  setCartSummary(cartSummary: any) {
    this.cartSummary.next(cartSummary);
  }

  getCartSummary(): Observable<any> {
    return this.cartSummary.asObservable();
  }

  getCartItemsData(): Observable<Array<CartItems>> {
    if (this.cartItems.value.length > 0) {
      return this.cartItems.asObservable();
    } else {
      this.getCartItems();
      return this.cartItems.asObservable();
    }
  }

  get categoryList(): Observable<any> {
    if (!this.categoriesList.value) {
      return this.http
        .get(`${environment.BASE_URL}${GeneralApiEndpoints.CATEGORIES}`)
        .pipe(
          map((res: APIResponse) => res.data),
          tap((data: Array<categories>) => {
            this.setCategories(data); // Update the BehaviorSubject for future calls
          }),
          catchError((error: any) => {
            this.globalUtilityService.showToast(
              'error',
              'Unable to Fetch Categories'
            );
            return of([]); // Return empty array on error
          })
        );
    }

    // If categories are already loaded, return the BehaviorSubject as Observable
    return this.categoriesList.asObservable();
  }

  setCategories(data: Array<categories>) {
    this.categoriesList.next(data);
  }

  getBanners(screen: number) {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.BANNERS}`;
    return this.http.get(url, { screen }).pipe(
      catchError((error: any) => {
        throw new Error(error);
      })
    );
  }

  getFeaturedCategoryProducts() {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.FEATURED_CATEGORY_PRODUCTS}`;
    return this.http.get(url).pipe(
      catchError((error: any) => {
        throw new Error(error);
      })
    );
  }

  getCustomerFeedbacks() {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.CUSTOMER_FEEDBACKS}`;
    return this.http.get(url).pipe(
      catchError((error: any) => {
        throw new Error(error);
      })
    );
  }

  updateFeedback(data: { rating: number; title: string; description: string }) {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.UPDATE_FEEDBACK}`;
    this.globalUtilityService.showLoader();
    return this.http.post(url, data, true).pipe(
      tap((res: APIResponse) => {
        this.globalUtilityService.hideLoader();
      }),
      catchError((error: any) => {
        this.globalUtilityService.hideLoader();
        throw new Error(error);
      })
    );
  }

  productList(params: productParams) {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.PRODUCT_LIST}`;
    return this.http.post(url, params).pipe(
      catchError((error: any) => {
        throw new Error(error);
      })
    );
  }

  productInfo(data: { slug?: string; size?: string }) {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.PRODUCT_INFO}`;
    return this.http.get(url, data).pipe(
      catchError((error: any) => {
        throw new Error(error);
      })
    );
  }

  addProductReview(data: {
    product_id: number;
    review: string;
    rating: number;
  }) {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.ADD_PRODUCT_REVIEW}`;
    return this.http.post(url, data).pipe(
      catchError((error: any) => {
        throw new Error(error);
      })
    );
  }

  uploadDocuments(data: FormData) {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.UPLOAD_DOCUMENTS}`;
    return this.http.post(url, data, true, false, true).pipe(
      catchError((error: any) => {
        throw new Error(error);
      })
    );
  }

  getCartItemCount(): Observable<number> {
    if (this.globalUtilityService.isUserLoggedIn()) {
      let cartItems =
        this.globalUtilityService.getDataFromLocalStorage('_cartItems');
      cartItems = JSON.parse(cartItems || '[]');

      if (cartItems && cartItems.length > 0) {
      }
      const url = `${environment.BASE_URL}${GeneralApiEndpoints.GET_CART_COUNT}`;
      return this.http.get(url, {}, true).pipe(
        map((res: APIResponse) => {
          this.cartItemCount.next(res.data?.cart_items_count || 0);
          return res.data?.cart_items_count || 0;
        }),
        catchError((error: any) => {
          throw new Error(error);
        })
      );
    } else {
      let cartItems =
        this.globalUtilityService.getDataFromLocalStorage('_cartItems');
      cartItems = JSON.parse(cartItems || '[]');
      if (cartItems && cartItems.length > 0) {
        this.cartItemCount.next(cartItems.length);
      }
      return this.cartItemCount.asObservable();
    }
  }

  updateCart(
    product_size_id: number,
    product_id: number,
    count: number,
    op: number
  ): Observable<any> {
    if (!product_id || !product_size_id) {
      return of(false);
    }

    if (this.globalUtilityService.isUserLoggedIn()) {
      const url = `${environment.BASE_URL}${GeneralApiEndpoints.UPDATE_CART}`;
      return this.http
        .post(url, { item: { product_size_id, product_id, count, op } }, true)
        .pipe(
          tap({
            next: (response: APIResponse) => {
              this.cartItemCount.next(response.data?.cart_items.length || 0);
              this.setCartItems(response.data?.cart_items);
              this.cartUpdated.next(true);
              this.globalUtilityService.setDataOnLocalStorage(
                '_cartItems',
                JSON.stringify(response.data?.cart_items)
              );
            },
            error: (error) => {
              this.globalUtilityService.showToast('error', error.error.message);
            },
            complete: () => {},
          })
        );
    } else {
      let cartItems =
        this.globalUtilityService.getDataFromLocalStorage('_cartItems');
      cartItems = JSON.parse(cartItems || '[]');

      if (cartItems && cartItems.length > 0) {
        const index = cartItems.findIndex(
          (item: any) => item.product_size_id === product_size_id
        );
        if (index >= 0) {
          if (op === 1) {
            cartItems[index].quantity += count;
          } else {
            let quant =
              cartItems[index].quantity >= count
                ? cartItems[index].quantity - count
                : 0;
            if (quant === 0) {
              cartItems = cartItems.filter(
                (item: any) => item.product_size_id != product_size_id
              );
            } else {
              cartItems[index].quantity = quant;
            }
          }
        } else {
          if (op === 1) {
            cartItems.push({
              quantity: count,
              product_id: product_id,
              product_size_id: product_size_id,
            });
          }
        }
      } else {
        if (op === 1) {
          cartItems = [
            {
              quantity: count,
              product_id: product_id,
              product_size_id: product_size_id,
            },
          ];
        }
      }

      this.cartItemCount.next(cartItems.length);
      this.setCartItems(cartItems);

      if (cartItems.length > 0) {
        this.globalUtilityService.setDataOnLocalStorage(
          '_cartItems',
          JSON.stringify(cartItems)
        );
      } else {
        this.globalUtilityService.removeDataFromLocalStorage('_cartItems');
      }
      this.cartUpdated.next(true);
    }
    return of(true);
  }

  removeProductFromCart(product_size_id: number): Observable<any> {
    if (this.globalUtilityService.isUserLoggedIn()) {
      const url = `${environment.BASE_URL}${GeneralApiEndpoints.REMOVE_CART_ITEM}`;
      return this.http.post(url, { product_size_id }, true).pipe(
        tap({
          next: (res: APIResponse) => {
            if (res.success) {
              this.cartItemCount.next(res.data.cart_count);
              this.setCartItems(res.data.cart_items);
              this.cartUpdated.next(true);
            }
          },
          error: (error: any) => {
            this.globalUtilityService.showToast('error', error.error.message);
          },
          complete: () => {
            return true;
          },
        }),
        catchError((error: any) => {
          throw new Error(error);
        })
      );
    } else {
      let cartItems =
        this.globalUtilityService.getDataFromLocalStorage('_cartItems');

      cartItems = JSON.parse(cartItems);

      if (!cartItems) {
        return of(false);
      }

      cartItems = cartItems.filter(
        (item: any) => item.product_size_id != product_size_id
      );

      this.globalUtilityService.setDataOnLocalStorage(
        '_cartItems',
        JSON.stringify(cartItems)
      );
      this.cartItemCount.next(cartItems.length);
      this.setCartItems(cartItems);
      this.cartUpdated.next(true);

      return of(true);
    }
  }

  clearCart() {
    let cartItems =
      this.globalUtilityService.getDataFromLocalStorage('_cartItems');
    cartItems = JSON.parse(cartItems || '[]');
    if (cartItems && cartItems.length > 0) {
      cartItems = [];
      this.globalUtilityService.setDataOnLocalStorage(
        '_cartItems',
        JSON.stringify(cartItems)
      );
      this.cartItemCount.next(0);
      this.setCartItems(cartItems);
    }
  }

  getCartItems(): Observable<any> {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.GET_CART_ITEMS}`;
    if (this.globalUtilityService.isUserLoggedIn()) {
      let cart_items =
        this.globalUtilityService.getDataFromLocalStorage('_cartItems');
      cart_items = JSON.parse(cart_items || '[]');
      let mergeCart = true;
      if (cart_items.length < 1) {
        mergeCart = false;
      }

      return this.http.post(url, { cart_items, mergeCart }, true).pipe(
        tap((res: APIResponse) => {
          this.cartItemCount.next(res.data?.cart_items.length);
          this.globalUtilityService.removeDataFromLocalStorage(
            LocalStorageDataModel.CART_ITEMS
          );
          this.setCartItems(res.data?.cart_items);
          this.setCartSummary(res.data?.cartSummary);
          // this.globalUtilityService.setDataOnLocalStorage(
          //   LocalStorageDataModel.CART_ITEMS,
          //   JSON.stringify(res.data.cart_items)
          // );
        }),
        catchError((error: any) => {
          throw new Error(error);
        })
      );
    } else {
      let cart_items =
        this.globalUtilityService.getDataFromLocalStorage('_cartItems');

      if (cart_items && cart_items.length > 0) {
        cart_items = JSON.parse(cart_items);
      } else {
        cart_items = [];
      }

      if (cart_items.length < 1) {
        return of([]);
      }

      return this.http.post(url, { cart_items }).pipe(
        tap((res: APIResponse) => {
          this.cartItemCount.next(res.data?.cart_items.length);
          this.setCartItems(cart_items);
          this.setCartSummary(res.data?.cartSummary);
        }),
        catchError((error: any) => {
          throw new Error(error);
        })
      );
    }
  }

  fetchBlogs(): Observable<any> {
    if (!this.blogListSubject.value) {
      this.getBlogList(100, 1, '').subscribe({
        next: (data: any) => {
          this.blogListSubject.next(data.data);
        },
        error: (error: any) => {
          this.globalUtilityService.showToast('Error', error.message);
        },
      });
    }
    return this.blogList$;
  }

  setBlogs(blogs: any[]) {
    this.blogListSubject.next(blogs);
  }

  getBlogList(limit: number, page_no: number, searchText: string) {
    // this.globalUtilityService.showLoader();
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.BLOG_LIST}`;
    return this.http.get(url, { limit, page_no, searchText }).pipe(
      tap((res: APIResponse) => {
        this.globalUtilityService.hideLoader();
      }),
      catchError((error: any) => {
        this.globalUtilityService.hideLoader();
        throw new Error(error);
      })
    );
  }

  getBlogDetails(id: number) {
    this.globalUtilityService.showLoader();
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.GET_BLOGS_INFO}`;
    return this.http.get(url, { id }).pipe(
      tap((res: APIResponse) => {
        this.globalUtilityService.hideLoader();
      }),
      catchError((error: any) => {
        this.globalUtilityService.hideLoader();
        throw new Error(error);
      })
    );
  }

  contactUs(data: {
    full_name: string;
    email: string;
    message: string;
    phone: string;
  }) {
    this.globalUtilityService.showLoader();
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.CONTACT_US}`;
    return this.http.post(url, data).pipe(
      tap((res: APIResponse) => {
        this.globalUtilityService.hideLoader();
      }),
      catchError((error: any) => {
        this.globalUtilityService.hideLoader();
        throw new Error(error);
      })
    );
  }

  setMashhoorplusImages(data: Array<MashhoorplusImages>) {
    this.mashhoorplusImages.next(data);
  }

  get MashhoorplusImages() {
    if (!this.mashhoorplusImages.getValue().length) {
      this.getLogos();
      return this.mashhoorplusImages.asObservable();
    }
    return this.mashhoorplusImages.asObservable();
  }

  getLogos() {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.BRAND}`;
    return this.http
      .get(url)
      .pipe(
        tap((data: APIResponse) => {
          if (data) {
            return this.setMashhoorplusImages(data.data);
          }
        })
      )
      .subscribe((data) => {
        console.log(data);
      });
  }

  getStates() {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.STATES_LIST}`;
    return this.http.get(url);
  }

  getCities(state_id: number) {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.CITIES_LIST}`;
    return this.http.get(url, { id: state_id });
  }

  checkPincodeServiceability(pincode: string) {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.CHECK_PINCODE}`;
    return this.http.get(url, { pincode });
  }

  getPinCodeData(pincode: string) {
    const url = `${environment.GET_PINCODE_DETAILS}/${pincode}`;
    return this.http.get(url);
  }

  getReviews(product_id: number, rating: number): Observable<any> {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.REVIEWS}`;
    return this.http.get(url, { product_id, rating }).pipe(
      catchError((error: any) => {
        throw new Error(error);
      })
    );
  }

  getUserDeliveryInfo(awbn: string) {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.TRACKING}`;
    return this.http.get(url, { awbn }).pipe(
      catchError((error: any) => {
        throw new Error(error);
      })
    );
  }

  getFaq() {
    const url = `${environment.BASE_URL}${GeneralApiEndpoints.FAQ}`;
    return this.http.get(url).pipe(
      catchError((error: any) => {
        throw new Error(error);
      })
    );
  }
}
